import {
  BookOpen,
  Briefcase,
  Compass,
  FlaskConical,
  Layers,
  Leaf,
  Monitor,
  Palette,
  Trophy,
  Utensils,
  type LucideIcon,
} from 'lucide-react'

// ── Tint derivation ───────────────────────────────────────────────────────────
// Takes the last 8 hex chars of the UUID (strip hyphens), interprets as an
// unsigned int, mods by 8. Stable across devices/sessions; never re-derives on
// rename or folder move (depends only on the immutable id).
function tintIndex(deckId: string): number {
  const hex = deckId.replace(/-/g, '').slice(-8)
  return parseInt(hex, 16) % 8
}

// ── System deck category icons ────────────────────────────────────────────────
// Keys match the `topic` / `category` values used on system decks.
// All icons are lucide-react; one stroke weight; currentColor (Wave 4 §1).
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  vocabulary: BookOpen,
  words: BookOpen,
  business: Briefcase,
  travel: Compass,
  food: Utensils,
  arts: Palette,
  science: FlaskConical,
  technology: Monitor,
  sports: Trophy,
  nature: Leaf,
}

// ── Size config ───────────────────────────────────────────────────────────────
const SIZE_CONFIG = {
  list:    { dim: 40, iconSize: 18, letterSize: 17, letterWeight: 600, radius: 8  },
  catalog: { dim: 56, iconSize: 24, letterSize: 22, letterWeight: 600, radius: 12 },
  header:  { dim: 80, iconSize: 32, letterSize: 28, letterWeight: 700, radius: 16 },
} as const

export type DeckCoverSize = keyof typeof SIZE_CONFIG

interface Props {
  /** Immutable deck UUID — drives tint derivation. */
  deckId: string
  /** Deck title — initial letter used as glyph for user decks. */
  title: string
  /** System decks show a category icon; user decks show the initial letter. */
  isSystem?: boolean
  /**
   * Category key for system deck icon resolution (e.g. "vocabulary", "business").
   * Falls back to the default Layers icon when absent or unrecognised.
   */
  category?: string
  size?: DeckCoverSize
  className?: string
  style?: React.CSSProperties
}

/**
 * Deterministic deck cover (Wave 4 §2).
 *
 * Background: one of 8 low-saturation, accent-adjacent tints derived from the
 * stable deck id so the cover never changes on rename or folder move.
 * Foreground: category icon for system decks; initial letter for user decks.
 *
 * Renders identically at list (40px), catalog (56px), and header (80px) sizes —
 * one source of truth, no per-screen re-implementations. Both themes get
 * deliberate tint values (Wave 3 §6 parity standard — not mechanical inversions).
 */
export default function DeckCover({
  deckId,
  title,
  isSystem = false,
  category,
  size = 'list',
  className,
  style,
}: Props) {
  const idx = tintIndex(deckId)
  const cfg = SIZE_CONFIG[size]

  const bg = `var(--cover-tint-${idx}-bg)`
  const fg = `var(--cover-tint-${idx}-fg)`

  // System deck: pick category icon (Layers is the generic fallback for "deck collection").
  // Ternary (not `&&`) so an empty-string category falls back to Layers instead of "".
  const SystemIcon: LucideIcon =
    (category ? CATEGORY_ICONS[category.toLowerCase()] : undefined) ?? Layers

  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        width: cfg.dim,
        height: cfg.dim,
        flexShrink: 0,
        borderRadius: cfg.radius,
        background: bg,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        ...style,
      }}
    >
      {isSystem ? (
        <SystemIcon size={cfg.iconSize} strokeWidth={1.75} />
      ) : (
        <span
          style={{
            fontSize: cfg.letterSize,
            fontWeight: cfg.letterWeight,
            fontFamily: 'var(--font-ui)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
          }}
        >
          {(title.trim()[0] ?? '?')}
        </span>
      )}
    </div>
  )
}
